import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Check, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { safeStorage } from '@/utils/safeStorage';
import { track } from '@/lib/analytics';

interface NewsletterSignupProps {
  variant?: 'default' | 'blog';
  className?: string;
}

const NEWSLETTER_SUBSCRIBED_KEY = 'mw_newsletter_subscribed';

// Email validation - mirrors backend blocklist
const BLOCKED_DOMAINS = [
  'example.com', 'example.org', 'example.net', 'test.com', 'test.org', 'test.net',
  'fake.com', 'no.com', 'none.com', 'noemail.com', 'nowhere.com', 'nope.com',
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'temp-mail.org',
  '10minutemail.com', 'throwaway.email', 'yopmail.com', 'trashmail.com',
  'sharklasers.com', 'getnada.com', 'maildrop.cc', 'fakeinbox.com'
];

const BLOCKED_LOCAL_PARTS = [
  'test', 'asdf', 'fake', 'none', 'no', 'spam', 'trash', 'abc', 'qwerty',
  'testing', 'temp', 'example', 'admin', 'user', 'demo', 'sample', 'noreply'
];

const validateEmail = (email: string): { valid: boolean; error?: string } => {
  const normalized = email.toLowerCase().trim();
  if (!normalized || !normalized.includes('@')) {
    return { valid: false, error: 'Please enter a valid email address' };
  }

  const [localPart, domain] = normalized.split('@');

  if (BLOCKED_DOMAINS.some(d => domain === d || domain.endsWith(`.${d}`))) {
    return { valid: false, error: 'Please use a real email address' };
  }

  if (BLOCKED_LOCAL_PARTS.includes(localPart) || /^test\d*$/i.test(localPart)) {
    return { valid: false, error: 'Please use a real email address' };
  }

  return { valid: true };
};

const extractErrorMessage = (error: unknown, data: unknown): string => {
  const dataObj = data as { error?: string } | null | undefined;
  if (dataObj?.error) return dataObj.error;

  const err = error as {
    message?: string;
    context?: { json?: { error?: string }; body?: string };
  } | null | undefined;
  if (err?.message) {
    const msg = err.message;
    const jsonMatch = msg.match(/\{[^{}]*"error"[^{}]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.error) return parsed.error;
      } catch { /* noop */ }
    }
    if (msg.includes('real email')) return 'Please use a real email address';
    if (msg.includes('valid email')) return 'Please use a valid email address';
  }

  if (err?.context?.json?.error) return err.context.json.error;
  if (err?.context?.body) {
    try {
      const parsed = JSON.parse(err.context.body);
      if (parsed.error) return parsed.error;
    } catch { /* noop */ }
  }

  return 'Something went wrong. Please try again.';
};

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  variant = 'default',
  className
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const emailValidation = useMemo(() => validateEmail(email), [email]);

  useEffect(() => {
    const subscribed = safeStorage.getItem(NEWSLETTER_SUBSCRIBED_KEY);
    if (subscribed === 'true') {
      setIsSubscribed(true);
    }
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (newEmail.includes('@')) {
      const validation = validateEmail(newEmail);
      if (!validation.valid) {
        setInlineError(validation.error || 'Please use a real email address');
      } else {
        setInlineError(null);
      }
    } else {
      setInlineError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailValidation.valid) {
      setInlineError(emailValidation.error || 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setInlineError(null);

    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: email.trim(), source: variant === 'blog' ? 'blog' : 'inline_signup' }
      });

      if (data?.error) {
        setInlineError(data.error);
        return;
      }

      if (error) {
        const errorMsg = extractErrorMessage(error, data);
        setInlineError(errorMsg);
        return;
      }

      safeStorage.setItem(NEWSLETTER_SUBSCRIBED_KEY, 'true');
      setShowSuccess(true);
      setIsSubscribed(true);
      track('newsletter_signup');

      toast({
        title: "Welcome aboard!",
        description: "You're now subscribed to our newsletter.",
      });
    } catch (error) {
      console.error('Newsletter signup error:', error);
      const errorMsg = extractErrorMessage(error, null);
      setInlineError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const headlines = {
    default: {
      title: "Stay Under the Stars",
      subtitle: "Get exclusive astrophotography tips, celestial event alerts, and location guides delivered to your inbox."
    },
    blog: {
      title: "Never Miss a Post",
      subtitle: "Get notified when we publish new guides, tips, and astrophotography inspiration."
    }
  };

  return (
    <section className={cn(
      "relative overflow-hidden bg-navy text-white",
      className
    )}>
      <div className="relative z-10 px-6 py-10 md:px-12 md:py-12">
        {showSuccess ? (
          <div className="text-center animate-fade-in">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-900/30">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">You're In!</h3>
            <p className="text-white/80">
              Check your inbox for a welcome email. See you under the stars!
            </p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 max-w-4xl mx-auto">
            {/* Content */}
            <div className="flex-1 text-center lg:text-left">
              <span className="eyebrow text-gold-light mb-3 block">Newsletter</span>

              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {headlines[variant].title}
              </h3>

              <p className="text-white/80 mb-6 max-w-md mx-auto lg:mx-0">
                {headlines[variant].subtitle}
              </p>
            </div>

            {/* Form */}
            <div className="w-full lg:w-auto lg:min-w-[320px]">
              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    className={cn(
                      "flex-1 h-12 bg-white/15 backdrop-blur-sm border-white/25 text-white placeholder:text-white/60 focus:border-gold/50",
                      inlineError && "border-destructive focus:border-destructive"
                    )}
                    disabled={isLoading}
                  />
                  <Button
                    type="submit"
                    disabled={isLoading || (email.length > 0 && !emailValidation.valid)}
                    className="h-12 px-6"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Subscribing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="h-4 w-4" />
                        Subscribe
                      </span>
                    )}
                  </Button>
                </div>
                {inlineError && (
                  <p className="text-sm text-red-400">{inlineError}</p>
                )}
              </form>
              <p className="text-xs text-white/60 mt-3 text-center lg:text-left">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsletterSignup;
